// packages/ui/src/dialogStack.ts

/** A registered overlay. `container` lets focus fall back into the layer beneath when an opener is gone. */
export interface DialogEntry {
  container?: HTMLElement | null;
}

/**
 * Open overlays (Modal, ConfirmModal, Drawer and custom layers), oldest first. Only the last one
 * reacts to Escape (and the Tab trap), so a ConfirmModal opened over a Modal or Drawer closes alone
 * and only one focus trap is live. Page scroll is locked while the stack is non-empty, so closing
 * one of two layers never unlocks the page under the other.
 */
const openDialogs: DialogEntry[] = [];

// The body overflow from before the first layer locked the page, put back when the last one closes.
let overflowBeforeLock = '';

export function pushDialog(entry: DialogEntry): void {
  if (openDialogs.length === 0 && typeof document !== 'undefined') {
    overflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  openDialogs.push(entry);
}

export function removeDialog(entry: DialogEntry): void {
  const index = openDialogs.indexOf(entry);
  if (index === -1) return;
  openDialogs.splice(index, 1);
  if (openDialogs.length === 0 && typeof document !== 'undefined') {
    document.body.style.overflow = overflowBeforeLock;
  }
}

export function isTopDialog(entry: DialogEntry): boolean {
  return openDialogs[openDialogs.length - 1] === entry;
}

/** The container of the topmost layer that has one, for focus fallback. */
export function topDialogContainer(): HTMLElement | null {
  for (let i = openDialogs.length - 1; i >= 0; i--) {
    const container = openDialogs[i].container;
    if (container?.isConnected) return container;
  }
  return null;
}

/** Number of open layers, for tests that assert nothing leaked. */
export function openDialogCount(): number {
  return openDialogs.length;
}
